import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { categories, MAIN_SITE_URL } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <nav className="fixed top-0 left-0 w-full z-50 transition-all duration-300 bg-background-50/80 backdrop-blur-md">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          <a href="/" className="flex items-center gap-2 group">
            <span className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 tracking-tight">
              CSS<span className="text-primary-500">Spreadsheet</span>
            </span>
          </a>

          <div className="hidden md:flex items-center gap-8">
            <button
              onClick={() => navigate('/')}
              className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer"
            >
              Home
            </button>

            <div
              className="relative"
              onMouseEnter={() => setDropdownOpen(true)}
              onMouseLeave={() => setDropdownOpen(false)}
            >
              <button className="flex items-center gap-1 text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer">
                Categories
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {dropdownOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-56 bg-background-50 rounded-lg border border-background-200/70 shadow-lg py-2 max-h-80 overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors whitespace-nowrap"
                    >
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={() => navigate('/shopping-guide')}
              className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer"
            >
              Shopping Guide
            </button>

            <button
              onClick={() => navigate('/cssbuy-vs-kakobuy-spreadsheet')}
              className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer"
            >
              SEO Hub
            </button>

            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="bg-primary-500 text-background-50 px-5 py-2 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              cssbuy sheet
              <i className="ri-arrow-right-up-line ml-1.5"></i>
            </a>
          </div>

          <button
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-700 hover:text-foreground-950 transition-colors cursor-pointer"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70">
          <div className="px-4 py-4">
            <div className="flex items-center gap-3 mb-4">
              <button
                onClick={() => { navigate('/'); setMobileMenuOpen(false); }}
                className="flex-1 py-2.5 text-sm font-semibold text-center bg-background-100 text-foreground-900 rounded-lg hover:bg-background-200 transition-colors cursor-pointer"
              >
                <i className="ri-home-4-line mr-1.5"></i>Home
              </button>
              <button
                onClick={() => { navigate('/shopping-guide'); setMobileMenuOpen(false); }}
                className="flex-1 py-2.5 text-sm font-semibold text-center bg-primary-50 text-primary-700 rounded-lg hover:bg-primary-100 transition-colors cursor-pointer"
              >
                <i className="ri-book-open-line mr-1.5"></i>Shopping Guide
              </button>
            </div>

            <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wide mb-2 px-1">Categories</p>
            <div className="grid grid-cols-2 gap-1.5 mb-4">
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2 px-2.5 py-2 text-sm text-foreground-600 hover:text-primary-600 hover:bg-primary-50 rounded-md transition-colors whitespace-nowrap"
                >
                  <span className="w-5 h-5 flex items-center justify-center text-accent-500 flex-shrink-0">
                    <i className={`${getCategoryIcon(cat.name)} text-xs`}></i>
                  </span>
                  {cat.name}
                </a>
              ))}
            </div>

            <div className="flex flex-col gap-2">
              <button
                onClick={() => { navigate('/cssbuy-vs-kakobuy-spreadsheet'); setMobileMenuOpen(false); }}
                className="py-2.5 text-sm font-medium text-foreground-700 hover:text-foreground-950 cursor-pointer"
              >
                <i className="ri-file-text-line mr-1.5"></i>SEO Hub
              </button>
              <a
                href={MAIN_SITE_URL}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center gap-1.5 bg-primary-500 text-background-50 px-5 py-3 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all"
              >
                cssbuy sheet
                <i className="ri-arrow-right-up-line"></i>
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}

function getCategoryIcon(name: string): string {
  const map: Record<string, string> = {
    'Shoes': 'ri-footprint-line',
    'Hoodies/Sweaters': 'ri-t-shirt-line',
    'T-Shirts': 'ri-t-shirt-2-line',
    'Jackets': 'ri-user-3-line',
    'Pants/Shorts': 'ri-pantone-line',
    'Headwear': 'ri-customer-service-line',
    'Sets': 'ri-stack-line',
    'Underwear/Underpants': 'ri-shirt-line',
    'Jersey': 'ri-team-line',
    'Accessories': 'ri-glasses-line',
    'Bags': 'ri-shopping-bag-3-line',
    'Belts': 'ri-link',
    'Perfumes': 'ri-drop-line',
    'Other Stuff': 'ri-more-line',
  };
  return map[name] || 'ri-store-line';
}